/**
 * <sd-shipping-protect-default> — turns the shipping-insurance switch ON by
 * default, once per session.
 *
 * This is an OPT-OUT CHARGE, so the whole design is about the one failure mode
 * that actually matters: adding a paid line back after the shopper removed it.
 * A naive "if not in cart, add it" would do exactly that — the shopper flips
 * the switch off, the cart re-renders, this element sees no insurance line and
 * silently re-adds it. So the auto-add is gated on a remembered decision, not
 * on cart state alone:
 *
 * - It adds at most ONCE per session (SESSION_KEY), and only while the shopper
 *   has never touched the switch themselves.
 * - The moment the switch is turned OFF by hand, that is recorded (OPT_OUT_KEY)
 *   and no auto-add ever fires again for the rest of the session.
 * - It never fires on an empty bag: there is nothing to insure, and the switch
 *   itself does not render in that state.
 *
 * sessionStorage rather than localStorage on purpose. The opt-out must outlive
 * page navigations (the shopper turns it off in the drawer, then loads /cart),
 * but a decision made weeks ago should not silently suppress the feature on a
 * brand-new visit. Session scope matches how the cart itself behaves.
 *
 * It reuses the switch's own AJAX contract by driving the real checkbox — it
 * does NOT post to /cart/add itself. Clicking the input runs <sd-shipping-
 * protect>.toggle(), so cart repaint, the live region, the error rollback and
 * the in-flight guard all come along for free and there is exactly one code
 * path that adds this line.
 *
 * Markup contract:
 *   <sd-shipping-protect-default hidden data-target="sd-ship-protect-drawer">
 */

import { Component } from '@theme/component';

const SESSION_KEY = 'sd:ship-protect:auto-added';
const OPT_OUT_KEY = 'sd:ship-protect:opted-out';

/** sessionStorage throws in Safari private mode and when cookies are blocked. */
const store = {
  /** @param {string} key */
  get(key) {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  },
  /** @param {string} key */
  set(key) {
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      // No storage: the in-memory guards below still prevent a loop within this
      // page view. Worst case the auto-add runs again on the next navigation,
      // which is why #hasOptedOut is also re-checked against live cart state.
    }
  },
};

class SdShippingProtectDefault extends Component {
  /** Guards against a second run inside this page view, storage or not. */
  #ran = false;

  connectedCallback() {
    super.connectedCallback();
    this.#maybeAdd();
  }

  /**
   * The switch this element belongs to. Resolved by id rather than held as a
   * ref: the two elements are siblings in the summary, and morph can replace
   * either one independently.
   *
   * @returns {HTMLInputElement | null}
   */
  get #input() {
    const id = this.dataset.target;
    if (!id) return null;
    const el = document.getElementById(id);
    return el instanceof HTMLInputElement ? el : null;
  }

  /**
   * Re-runs after a cart re-render. The listener that records an opt-out lives
   * here rather than in connectedCallback because morph may swap the input
   * without ever disconnecting this element.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#maybeAdd();
  }

  #maybeAdd() {
    if (this.#ran) return;

    const input = this.#input;
    if (!input) return;

    // Watch for the shopper turning it off by hand. Registered before the
    // early returns below so the opt-out is recorded even on a page where the
    // auto-add itself is a no-op (already on, already run).
    this.#watch(input);

    if (store.get(SESSION_KEY) || store.get(OPT_OUT_KEY)) return;
    // Already on — either Liquid rendered it checked or another surface added
    // it. Mark the session done so this never competes with that state.
    if (input.checked) {
      store.set(SESSION_KEY);
      return;
    }
    // The switch is mid-request; its own updatedCallback will settle the state
    // and this will be re-entered from the next render.
    if (input.disabled) return;

    this.#ran = true;
    store.set(SESSION_KEY);

    // Drive the real control so <sd-shipping-protect> owns the mutation. Not
    // input.checked = true + dispatchEvent: .click() flips the checkbox AND
    // fires a trusted-shaped change event in one step, which is exactly what a
    // shopper toggling it does.
    input.click();
  }

  /** @param {HTMLInputElement} input */
  #watch(input) {
    // The element may be re-registered against the same input after a morph
    // that did not replace it; a dataset flag is cheaper than tracking
    // AbortControllers per input.
    if (input.dataset.sdDefaultWatched === 'true') return;
    input.dataset.sdDefaultWatched = 'true';

    input.addEventListener('change', () => {
      // Only a manual OFF is an opt-out. Turning it back on is not an opt-in
      // to future auto-adds; SESSION_KEY already stops those either way.
      if (!input.checked) store.set(OPT_OUT_KEY);
    });
  }
}

if (!customElements.get('sd-shipping-protect-default')) {
  customElements.define('sd-shipping-protect-default', SdShippingProtectDefault);
}
